import React, { useState, useEffect } from 'react';
import { Calendar, IndianRupee, FileText, Droplets, Wallet, Plus, Trash2, ArrowRight, Loader2, Save, Landmark, Lock, Unlock } from 'lucide-react';

const DailySheet = () => {
  const [date, setDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toISOString().split('T')[0];
  });
  const [salesData, setSalesData] = useState({ expectedRevenue: 0, netLiters: 0 });
  const [collections, setCollections] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [regularExpenses, setRegularExpenses] = useState({ density: '', food: '', tanker: '' });
  const [receipts, setReceipts] = useState([]);
  const [tanks, setTanks] = useState({});
  const [detailedData, setDetailedData] = useState({ pumps: [], products: [] });
  const [rates, setRates] = useState({});
  const [openingAmount, setOpeningAmount] = useState('');
  const [isOpeningLocked, setIsOpeningLocked] = useState(false);
  const [lubes, setLubes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [creditorList, setCreditorList] = useState([]);
  const [lubeSales, setLubeSales] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [settlements, setSettlements] = useState({ credit: '', paytm: '', icici: '', ccms: '', bank: '' });
  const [isGlobalSaving, setIsGlobalSaving] = useState(false);
  const [isSheetLocked, setIsSheetLocked] = useState(false);
  const [lockState, setLockState] = useState({ isLocked: false, adminOverride: false, isAutoLocked: false });
  const userRole = localStorage.getItem('role');

  const [newExpense, setNewExpense] = useState({ description: '', amount: '' });
  const [newCollection, setNewCollection] = useState({ employee_id: '', employee_name: '', shortage: '' });
  const [newReceipt, setNewReceipt] = useState({ tank_id: '', volume: '', invoice: '' });
  const [newLubeSale, setNewLubeSale] = useState({ lube_id: '', quantity: '', rate: '' });
  const [creditorPayments, setCreditorPayments] = useState([]);
  const [newCreditorPayment, setNewCreditorPayment] = useState({ creditor_name: '', amount: '' });

  useEffect(() => {
    fetchDailyData();
  }, [date]);

  const fetchDailyData = async () => {
    setIsLoading(true);
    try {
      // 1. Fetch the raw readings to calculate Expected Sales
      const tomorrowDate = new Date(date);
      tomorrowDate.setDate(tomorrowDate.getDate() + 1);
      const tomorrowStr = tomorrowDate.toISOString().split('T')[0];
      
      const readingsRes = await fetch(`/api/readings?date=${tomorrowStr}`, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Accept': 'application/json'
        }
      });
      const readingsJson = await readingsRes.json();

      // 2. Fetch Fuel Rates
      const ratesRes = await fetch(`/api/rates?date=${date}`, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Accept': 'application/json'
        }
      });
      const ratesJson = await ratesRes.json();
      const ratesObj = {
        'Petrol': parseFloat(ratesJson.petrol) || 0,
        'Diesel': parseFloat(ratesJson.diesel) || 0,
        'XG HSD': parseFloat(ratesJson.xtragreen) || 0,
        'Xtragreen': parseFloat(ratesJson.xtragreen) || 0,
        petrol: parseFloat(ratesJson.petrol) || 0,
        diesel: parseFloat(ratesJson.diesel) || 0,
        xtragreen: parseFloat(ratesJson.xtragreen) || 0
      };
      setRates(ratesObj);

      // 3. Fetch Financial Sheet Data (Expenses, Collections)
      const sheetRes = await fetch(`/api/daily-sheet?date=${date}`, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Accept': 'application/json'
        }
      });
      const sheetJson = await sheetRes.json();

      // Calculate Expected Sales
      let totalExpected = 0;
      let totalLiters = 0;
      
      const tData = readingsJson.today || {};
      const yData = readingsJson.yesterday || {};
      const testing = tData.globalTesting || {};

      // We need nozzle fuel types. Let's fetch config to map them.
      const configRes = await fetch(`/api/config`, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Accept': 'application/json'
        }
      });
      const configJson = await configRes.json();
      
      const pumpsArray = [];
      const productMap = {};

      configJson.tanks?.forEach(t => {
         if(!productMap[t.fuel_type]) productMap[t.fuel_type] = { open: 0, close: 0, testing: 0, sale: 0, income: 0 };
         const tDip = tData.tanks?.[t.id] || 0;
         const yDip = yData.tanks?.[t.id] || 0;
         productMap[t.fuel_type].close += parseFloat(tDip) || 0;
         productMap[t.fuel_type].open += parseFloat(yDip) || 0;
      });

      configJson.pumps?.forEach(pump => {
        const pumpData = { name: pump.pump_name, nozzles: [] };
        pump.nozzles?.forEach(noz => {
          const tRead = tData.nozzles?.[noz.id]?.reading || 0;
          const yRead = yData.nozzles?.[noz.id]?.reading || 0;
          const sales = (parseFloat(tRead) || 0) - (parseFloat(yRead) || 0);
          
          pumpData.nozzles.push({
            name: `${noz.fuel_type} (${noz.nozzle_number})`,
            open: parseFloat(yRead) || 0,
            close: parseFloat(tRead) || 0,
            sale: sales > 0 ? sales : 0
          });

          if (sales > 0) {
            totalLiters += sales;
            totalExpected += sales * (ratesObj[noz.fuel_type] || 0);
            if(productMap[noz.fuel_type]) {
               productMap[noz.fuel_type].sale += sales;
            }
          }
        });
        pumpsArray.push(pumpData);
      });

      sheetJson.receipts?.forEach(r => {
         const t = configJson.tanks?.find(tx => tx.id === r.tank_id);
         if(t && productMap[t.fuel_type]) {
            productMap[t.fuel_type].income += parseFloat(r.volume_received) || 0;
         }
      });

      Object.keys(testing).forEach(fuel => {
        const testLiters = parseFloat(testing[fuel]) || 0;
        const rate = ratesObj[fuel] || 0;
        totalExpected -= (testLiters * rate);
        if(productMap[fuel]) {
           productMap[fuel].testing = testLiters;
        }
      });

      const productsArray = Object.keys(productMap).map(fuel => {
         const d = productMap[fuel];
         return {
            name: fuel,
            open: d.open.toFixed(2),
            income: d.income.toFixed(2),
            testing: d.testing.toFixed(2),
            total: (d.open + d.income).toFixed(2),
            sale: d.sale.toFixed(2),
            close: d.close.toFixed(2)
         };
      });

      setDetailedData({ pumps: pumpsArray, products: productsArray });
      setSalesData({ expectedRevenue: totalExpected, netLiters: totalLiters });
      
      const rExp = { density: '', food: '', tanker: '' };
      const otherExp = [];
      (sheetJson.expenses || []).forEach(e => {
        const desc = (e.description || '').toLowerCase();
        if (desc === 'density') rExp.density = e.amount;
        else if (desc === 'food and tea' || desc === 'food & tea') rExp.food = e.amount;
        else if (desc === 'tanker expense') rExp.tanker = e.amount;
        else otherExp.push(e);
      });
      setRegularExpenses(rExp);
      setExpenses(otherExp);

      setCollections(sheetJson.collections || []);
      setCreditorPayments(sheetJson.creditor_payments || []);
      setReceipts(sheetJson.receipts || []);
      setLubeSales(sheetJson.lube_sales || []);
      
      if (sheetJson.yesterday_closing_cash !== null && sheetJson.yesterday_closing_cash !== undefined) {
        setOpeningAmount(sheetJson.yesterday_closing_cash);
        setIsOpeningLocked(true);
      } else if (sheetJson.settlements && sheetJson.settlements.opening_cash) {
        setOpeningAmount(sheetJson.settlements.opening_cash);
        setIsOpeningLocked(false);
      } else {
        setOpeningAmount('');
        setIsOpeningLocked(false);
      }

      const [year, month, day] = date.split('-').map(Number);
      const targetDate = new Date(year, month - 1, day);
      const today = new Date();
      targetDate.setHours(0,0,0,0);
      today.setHours(0,0,0,0);
      const diffDays = Math.round((today - targetDate) / (1000 * 60 * 60 * 24));
      const isAuto = diffDays > 1;

      if (sheetJson.settlements) {
        setSettlements(sheetJson.settlements);
        setLockState({
          isLocked: !!sheetJson.settlements.is_locked,
          adminOverride: !!sheetJson.settlements.admin_override,
          isAutoLocked: isAuto
        });
        const effectiveLock = userRole === 'admin' 
          ? !!sheetJson.settlements.is_locked 
          : (!!sheetJson.settlements.is_locked || (isAuto && !sheetJson.settlements.admin_override));
        setIsSheetLocked(effectiveLock);
      } else {
        setSettlements({ credit: '', paytm: '', icici: '', ccms: '', bank: '' });
        setLockState({ isLocked: false, adminOverride: false, isAutoLocked: isAuto });
        setIsSheetLocked(userRole !== 'admin' && isAuto);
      }
      
      const lubesRes = await fetch(`/api/lubes`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
      const lubesJson = await lubesRes.json();
      setLubes(lubesJson);

      const empRes = await fetch(`/api/employees`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
      const empJson = await empRes.json();
      setEmployees(empJson);
      
      const credRes = await fetch(`/api/creditors`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
      const credJson = await credRes.json();
      setCreditorList(credJson);
      
      const tankMap = {};
      configJson.tanks?.forEach(t => tankMap[t.id] = t.tank_name);
      setTanks(tankMap);

    } catch (err) {
      console.error(err);
    }
    setIsLoading(false);
  };

  const fetchOnlySheetData = async () => {
    try {
      const sheetRes = await fetch(`/api/daily-sheet?date=${date}`, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Accept': 'application/json'
        }
      });
      const sheetJson = await sheetRes.json();
      const rExp = { density: '', food: '', tanker: '' };
      const otherExp = [];
      (sheetJson.expenses || []).forEach(e => {
        const desc = (e.description || '').toLowerCase();
        if (desc === 'density') rExp.density = e.amount;
        else if (desc === 'food and tea' || desc === 'food & tea') rExp.food = e.amount;
        else if (desc === 'tanker expense') rExp.tanker = e.amount;
        else otherExp.push(e);
      });
      setRegularExpenses(rExp);
      setExpenses(otherExp);
      setCollections(sheetJson.collections || []);
      setCreditorPayments(sheetJson.creditor_payments || []);
      setReceipts(sheetJson.receipts || []);
      setLubeSales(sheetJson.lube_sales || []);
      
      if (sheetJson.yesterday_closing_cash !== null && sheetJson.yesterday_closing_cash !== undefined) {
        setOpeningAmount(sheetJson.yesterday_closing_cash);
        setIsOpeningLocked(true);
      } else if (sheetJson.settlements && sheetJson.settlements.opening_cash) {
        setOpeningAmount(sheetJson.settlements.opening_cash);
        setIsOpeningLocked(false);
      } else {
        setOpeningAmount('');
        setIsOpeningLocked(false);
      }

      const targetDate = new Date(date);
      const today = new Date();
      targetDate.setHours(0,0,0,0);
      today.setHours(0,0,0,0);
      const diffDays = (today - targetDate) / (1000 * 60 * 60 * 24);
      const isAuto = diffDays > 1;

      if (sheetJson.settlements) {
        setSettlements(sheetJson.settlements);
        setLockState({
          isLocked: !!sheetJson.settlements.is_locked,
          adminOverride: !!sheetJson.settlements.admin_override,
          isAutoLocked: isAuto
        });
        const effectiveLock = userRole === 'admin' 
          ? !!sheetJson.settlements.is_locked 
          : (!!sheetJson.settlements.is_locked || (isAuto && !sheetJson.settlements.admin_override));
        setIsSheetLocked(effectiveLock);
      } else {
        setSettlements({ credit: '', paytm: '', icici: '', ccms: '', bank: '' });
        setLockState({ isLocked: false, adminOverride: false, isAutoLocked: isAuto });
        setIsSheetLocked(userRole !== 'admin' && isAuto);
      }
    } catch (err) {
      console.error(err);
    }
  };


  const handleToggleLock = async () => {
    const isCurrentlyLockedForManager = lockState.isLocked || (lockState.isAutoLocked && !lockState.adminOverride);
    if (!window.confirm(`Are you sure you want to ${isCurrentlyLockedForManager ? 'UNLOCK' : 'LOCK'} this sheet for managers?`)) return;
    
    try {
      const payload = lockState.isAutoLocked 
        ? { date: date, admin_override: !lockState.adminOverride }
        : { date: date, is_locked: !lockState.isLocked };

      const res = await fetch('/api/daily-sheet/toggle-lock', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.status === 'success') {
        fetchDailyData();
      } else {
        alert('Error: ' + data.error);
      }
    } catch (err) {
      console.error(err);
      alert('Error toggling lock state');
    }
  };

  const handleAddCollection = async (e) => {
    e.preventDefault();
    const id = Date.now();
    setCollections([...collections, { id, employee_id: newCollection.employee_id, employee_name: newCollection.employee_name, shortage: newCollection.shortage }]);
    setNewCollection({ employee_id: '', employee_name: '', shortage: '' });
  };

  const handleAddCreditorPayment = async (e) => {
    e.preventDefault();
    const id = Date.now();
    setCreditorPayments([...creditorPayments, { id, creditor_name: newCreditorPayment.creditor_name, amount: newCreditorPayment.amount }]);
    setNewCreditorPayment({ creditor_name: '', amount: '' });
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    const id = Date.now();
    setExpenses([...expenses, { id, description: newExpense.description, amount: newExpense.amount }]);
    setNewExpense({ description: '', amount: '' });
  };

  const handleGlobalSave = async (closingCash) => {
    setIsGlobalSaving(true);
    try {
      const res = await fetch('/api/daily-sheet/save-all', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ 
          date: date, 
          expenses: (() => {
            const allExpenses = [...expenses];
            if (regularExpenses.density) allExpenses.push({ description: 'Density', amount: regularExpenses.density });
            if (regularExpenses.food) allExpenses.push({ description: 'Food and tea', amount: regularExpenses.food });
            if (regularExpenses.tanker) allExpenses.push({ description: 'Tanker expense', amount: regularExpenses.tanker });
            return allExpenses;
          })(), 
          collections, 
          creditorPayments,
          lubeSales, 
          settlements: {
            ...settlements,
            opening_cash: Number(openingAmount) || 0,
            closing_cash: closingCash || 0
          }
        })
      });
      const data = await res.json();
      if (data.status === 'success') {
        alert('Entire Daily Sheet saved successfully!');
        fetchDailyData();
      } else {
        alert('Error saving: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      console.error(err);
      alert('Error saving daily sheet.');
    }
    setIsGlobalSaving(false);
  };

    const handleAddLubeSale = async (e) => {
      e.preventDefault();
      try {
        const lube = lubes.find(l => l.id === Number(newLubeSale.lube_id));
        if (!lube) return;
        const rateVal = Number(newLubeSale.rate) || Number(lube.rate);
        const qtyVal = Number(newLubeSale.quantity) || 0;
        const amount = qtyVal * rateVal;
        const id = Date.now();
        
        setLubeSales([...lubeSales, { id, lube_id: lube.id, name: lube.name, pack_size: lube.pack_size, quantity: qtyVal, rate: rateVal, master_rate: lube.rate, discount: 0, amount }]);
        setNewLubeSale({ lube_id: '', quantity: '', rate: '' });
      } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (type, id) => {
    try {
      if (type === 'expenses') setExpenses(expenses.filter(e => e.id !== id));
      if (type === 'collections') setCollections(collections.filter(c => c.id !== id));
      if (type === 'lubes') setLubeSales(lubeSales.filter(l => l.id !== id));
      if (type === 'creditor_payments') setCreditorPayments(creditorPayments.filter(p => p.id !== id));
    } catch (err) {
      console.error(err);
    }
  };

  // Calculations
  const totalOtherExpenses = expenses.reduce((sum, e) => sum + Number(e.amount), 0);
  const totalRegularExpenses = Number(regularExpenses.density || 0) + Number(regularExpenses.food || 0) + Number(regularExpenses.tanker || 0);
  const totalExpenses = totalOtherExpenses + totalRegularExpenses;
  const totalSettlementBox = Number(settlements.credit || 0) + Number(settlements.paytm || 0) + Number(settlements.icici || 0) + Number(settlements.ccms || 0) + Number(settlements.bank || 0);
  const totalEmployeeShortages = collections.reduce((sum, c) => sum + Number(c.shortage || 0), 0);
  const totalCreditorPayments = creditorPayments.reduce((sum, p) => sum + Number(p.amount), 0);
  
  const totalFuelSales = detailedData.products.reduce((sum, prod) => sum + Math.round((parseFloat(prod.sale) - parseFloat(prod.testing || 0)) * (rates[prod.name] || 0)), 0);
  const totalLubeRevenue = lubeSales.reduce((sum, s) => sum + Number(s.amount), 0);
  
  const grandTotalExpected = totalFuelSales + totalLubeRevenue + Number(openingAmount || 0) + totalCreditorPayments;
  const totalSettled = totalSettlementBox + totalExpenses + totalEmployeeShortages;
  const expectedClosingCash = grandTotalExpected - totalSettled;

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center bg-white p-6 rounded-3xl shadow-sm border border-slate-100 gap-4">
        <div>
          <h1 className="text-2xl font-black text-slate-900 tracking-tight flex items-center gap-2">
            <FileText className="text-blue-600" size={28} />
            Daily Settlement Sheet
          </h1>
          <p className="text-slate-500 font-medium mt-1">Financial summary, cashiers, and stock variations.</p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full lg:w-auto">
          {userRole === 'admin' && (
            <button 
              onClick={handleToggleLock}
              className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-bold transition-all border ${lockState.isLocked || (lockState.isAutoLocked && !lockState.adminOverride) ? 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100' : 'bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200'}`}
            >
              {(lockState.isLocked || (lockState.isAutoLocked && !lockState.adminOverride)) ? <Lock size={18} /> : <Unlock size={18} />}
              {lockState.isAutoLocked 
                ? (lockState.adminOverride ? '🔓 OVERRIDDEN' : '🔒 AUTO-LOCKED') 
                : (lockState.isLocked ? '🔒 LOCKED' : '🔓 OPEN')}
            </button>
          )}
          <div className="flex items-center gap-3 bg-slate-50 p-2.5 rounded-2xl border border-slate-200">
            <Calendar className="text-slate-500 ml-3" size={20} />
            <input 
              type="date" 
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="bg-transparent border-none font-bold text-slate-700 focus:ring-0 cursor-pointer outline-none"
            />
          </div>
          <button 
            onClick={() => handleGlobalSave(expectedClosingCash)} 
            disabled={isGlobalSaving || isSheetLocked} 
            className={`relative overflow-hidden group font-bold py-3 px-8 rounded-2xl shadow-lg transition-all active:scale-95 disabled:opacity-70 flex items-center justify-center gap-2 border ${isSheetLocked ? 'bg-slate-300 text-slate-500 cursor-not-allowed border-slate-300 shadow-none' : 'bg-gradient-to-br from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white shadow-emerald-500/30 border-emerald-400/20'}`}
          >
            {!isSheetLocked && <div className="absolute inset-0 bg-white/20 group-hover:translate-x-full transition-transform duration-500 -translate-x-full skew-x-12" />}
            <Save size={20} className={`${isGlobalSaving ? 'animate-bounce' : (!isSheetLocked && 'group-hover:scale-110 transition-transform')}`} />
            <span className="tracking-wide">{isGlobalSaving ? 'SAVING DATA...' : 'SAVE SHEET'}</span>
          </button>
        </div>
      </div>

      {/* Detailed Data Tables (Meter Readings & Product Stock) */}
      <div className={`transition-all duration-300 ${isSheetLocked ? 'pointer-events-none opacity-90 grayscale-[15%]' : ''}`}>
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden relative min-h-[200px]">
        {isLoading && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] z-10 flex flex-col items-center justify-center">
            <Loader2 className="w-10 h-10 text-blue-500 animate-spin mb-2" />
            <span className="text-sm font-semibold text-slate-600">Loading data...</span>
          </div>
        )}
        <div className="p-5 border-b border-slate-100 bg-slate-50">
          <h2 className="font-bold text-slate-800 flex items-center gap-2">
            <Droplets className="text-blue-500" size={20} />
            Meter Readings & Stock Summary
          </h2>
        </div>
        <div className="p-5 space-y-8">
          
          {/* Meter Readings Table */}
          <div className="overflow-x-auto">
            <h3 className="font-semibold text-slate-700 mb-3">Nozzle Readings</h3>
            <table className="w-full text-center text-sm border-collapse border border-slate-200">
              <thead>
                <tr className="bg-slate-100">
                  <th className="border border-slate-200 p-2 text-left w-32 font-bold text-slate-700">Meter</th>
                  {detailedData.pumps.map(pump => (
                    pump.nozzles.map((noz, i) => (
                      <th key={noz.name + i} className="border border-slate-200 p-2 text-slate-600 font-semibold">{noz.name}</th>
                    ))
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-slate-200 p-2 text-left font-semibold text-slate-600">Close</td>
                  {detailedData.pumps.map(pump => (
                    pump.nozzles.map((noz, i) => (
                      <td key={noz.name + i} className="border border-slate-200 p-2 font-mono">{noz.close}</td>
                    ))
                  ))}
                </tr>
                <tr>
                  <td className="border border-slate-200 p-2 text-left font-semibold text-slate-600">Open</td>
                  {detailedData.pumps.map(pump => (
                    pump.nozzles.map((noz, i) => (
                      <td key={noz.name + i} className="border border-slate-200 p-2 font-mono">{noz.open}</td>
                    ))
                  ))}
                </tr>
                <tr className="bg-yellow-100">
                  <td className="border border-slate-200 p-2 text-left font-black text-slate-800">SALE</td>
                  {detailedData.pumps.map(pump => (
                    pump.nozzles.map((noz, i) => (
                      <td key={noz.name + i} className="border border-slate-200 p-2 font-mono font-black text-slate-900">{noz.sale.toFixed(2)}</td>
                    ))
                  ))}
                </tr>
              </tbody>
            </table>
          </div>

          {/* Product Stock Table */}
          <div className="overflow-x-auto">
            <h3 className="font-semibold text-slate-700 mb-3">Product Stock Summary</h3>
            <table className="w-full text-center text-sm border-collapse border border-slate-200">
              <thead>
                <tr className="bg-slate-100">
                  <th className="border border-slate-200 p-2 text-left font-bold text-slate-700">Product</th>
                  <th className="border border-slate-200 p-2 text-slate-600 font-semibold">Testing</th>
                  <th className="border border-slate-200 p-2 text-slate-600 font-semibold">Open</th>
                  <th className="border border-slate-200 p-2 text-slate-600 font-semibold">Income</th>
                  <th className="border border-slate-200 p-2 text-slate-600 font-semibold">Total</th>
                  <th className="border border-slate-200 p-2 text-slate-600 font-semibold">Sale</th>
                  <th className="border border-slate-200 p-2 text-slate-600 font-semibold">Close</th>
                </tr>
              </thead>
              <tbody>
                {detailedData.products.map((prod, i) => (
                  <tr key={i}>
                    <td className="border border-slate-200 p-2 text-left font-bold text-slate-700 bg-slate-50">{prod.name}</td>
                    <td className="border border-slate-200 p-2 font-mono text-slate-500 bg-slate-50">{prod.testing}</td>
                    <td className="border border-slate-200 p-2 font-mono">{prod.open}</td>
                    <td className="border border-slate-200 p-2 font-mono text-emerald-600 bg-emerald-50">{prod.income}</td>
                    <td className="border border-slate-200 p-2 font-mono font-bold bg-slate-100">{prod.total}</td>
                    <td className="border border-slate-200 p-2 font-mono font-black text-blue-600 bg-blue-50">{prod.sale}</td>
                    <td className="border border-slate-200 p-2 font-mono font-bold">{prod.close}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column: Sales & Collections */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* Revenue Breakdown Table (Replacing Master Summary) */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden mb-6">
            <div className="p-5 border-b border-slate-100 bg-slate-50 flex justify-between items-center">
              <h2 className="font-bold text-slate-800 flex items-center gap-2">
                <FileText className="text-blue-500" size={20} />
                Revenue Breakdown
              </h2>
            </div>
            <table className="w-full text-left text-sm">
              <thead className="bg-white text-slate-500 font-semibold border-b border-slate-100">
                <tr>
                  <th className="p-4">Product Rate</th>
                  <th className="p-4 text-right">Net Liters</th>
                  <th className="p-4 text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {detailedData.products.map((prod, i) => {
                  const rate = rates[prod.name] || 0;
                  const netLiters = parseFloat(prod.sale) - parseFloat(prod.testing || 0);
                  const amount = Math.round(netLiters * rate);
                  
                  return (
                    <tr key={i} className="hover:bg-slate-50 transition-colors">
                      <td className="p-4 font-semibold text-slate-700 flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${prod.name.toLowerCase().includes('diesel') ? 'bg-blue-500' : 'bg-emerald-500'}`}></span>
                        {prod.name} (₹{rate.toFixed(2)})
                      </td>
                      <td className="p-4 font-mono text-slate-600 text-right">{netLiters.toFixed(2)} L</td>
                      <td className="p-4 font-mono font-bold text-slate-900 text-right">₹{amount.toLocaleString()}</td>
                    </tr>
                  );
                })}
                <tr className="bg-slate-50 border-t-2 border-slate-200">
                  <td colSpan="2" className="p-4 font-bold text-slate-700 text-right">
                     TOTAL LUBE SALES:
                  </td>
                  <td className="p-4 font-mono font-bold text-emerald-600 text-right">
                    ₹{totalLubeRevenue.toLocaleString()}
                  </td>
                </tr>
                <tr className="bg-slate-100 border-t border-slate-200">
                  <td colSpan="2" className="p-4 font-black text-slate-800 text-right">
                     TOTAL FUEL SALES:
                  </td>
                  <td className="p-4 font-mono font-black text-emerald-600 text-right">
                    ₹{detailedData.products.reduce((sum, prod) => sum + Math.round((parseFloat(prod.sale) - parseFloat(prod.testing || 0)) * (rates[prod.name] || 0)), 0).toLocaleString()}
                  </td>
                </tr>
                <tr className="bg-slate-50/50 border-t border-slate-200">
                  <td colSpan="2" className="p-4 font-bold text-slate-800 flex items-center gap-2">
                     <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                     OPENING (Previous Day Cash)
                  </td>
                  <td className="p-4 text-right">
                      <input 
                        type="number" 
                        value={openingAmount}
                        onChange={e => setOpeningAmount(e.target.value)}
                        disabled={isOpeningLocked}
                        className={`w-32 text-right bg-white border border-slate-200 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono font-bold shadow-sm ${isOpeningLocked ? 'text-slate-500 bg-slate-100 cursor-not-allowed' : 'text-slate-900'}`} 
                        placeholder="0.00" 
                      />
                  </td>
                </tr>
                <tr className="bg-emerald-50 border-t border-slate-200">
                  <td colSpan="2" className="p-4 font-bold text-slate-800 text-right">
                     CREDITOR PAYMENTS RECEIVED:
                  </td>
                  <td className="p-4 font-mono font-bold text-emerald-600 text-right">
                    ₹{totalCreditorPayments.toLocaleString()}
                  </td>
                </tr>
                <tr className="bg-slate-100 border-t-2 border-slate-200">
                  <td colSpan="2" className="p-4 font-black text-slate-800 text-right">GRAND TOTAL EXPECTED:</td>
                  <td className="p-4 font-mono font-black text-slate-900 text-right">₹{grandTotalExpected.toLocaleString()}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Creditor Payments (Inflow) */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden mb-6">
            <div className="p-5 border-b border-slate-100 bg-emerald-50 flex justify-between items-center">
              <h2 className="font-bold text-emerald-800 flex items-center gap-2">
                <Wallet className="text-emerald-500" size={20} />
                Creditor Payments Received
              </h2>
            </div>
            <div className="p-5">
              <form onSubmit={handleAddCreditorPayment} className="flex gap-3 mb-6">
                <select required className="input-field flex-1" value={newCreditorPayment.creditor_name} onChange={e => setNewCreditorPayment({...newCreditorPayment, creditor_name: e.target.value})}>
                  <option value="">Select Creditor...</option>
                  {creditorList.filter(c => c.status === 'Active').map(c => (
                    <option key={c.id} value={c.name}>{c.name}</option>
                  ))}
                </select>
                <input required type="number" step="0.01" placeholder="₹ Amount" className="input-field w-32" value={newCreditorPayment.amount} onChange={e => setNewCreditorPayment({...newCreditorPayment, amount: e.target.value})} />
                <button type="submit" className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold px-4 py-3 rounded-xl transition-colors shrink-0 flex items-center justify-center"><Plus size={20}/></button>
              </form>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50 text-slate-500 font-semibold">
                    <tr>
                      <th className="p-3 rounded-tl-lg">Creditor Name</th>
                      <th className="p-3">Amount Received</th>
                      <th className="p-3 rounded-tr-lg"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {creditorPayments.length === 0 ? (
                      <tr><td colSpan="3" className="p-4 text-center text-slate-500 italic">No payments recorded</td></tr>
                    ) : (
                      creditorPayments.map(p => (
                        <tr key={p.id} className="hover:bg-slate-50 transition-colors">
                          <td className="p-3 font-semibold text-slate-800">{p.creditor_name}</td>
                          <td className="p-3 font-mono font-bold text-emerald-600">₹{p.amount}</td>
                          <td className="p-3 text-right">
                            <button onClick={() => handleDelete('creditor_payments', p.id)} className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg"><Trash2 size={16}/></button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Lube Sales */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden mb-6">
            <div className="p-5 border-b border-slate-100 bg-slate-50 flex justify-between items-center">
              <h2 className="font-bold text-slate-800 flex items-center gap-2">
                <Droplets className="text-amber-500" size={20} />
                Lube Sales
              </h2>
            </div>
            <div className="p-5">
              <form onSubmit={handleAddLubeSale} className="flex flex-col md:flex-row gap-4 mb-6">
                <div className="flex-1">
                  <select 
                    required
                    className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none"
                    value={newLubeSale.lube_id}
                    onChange={e => {
                      const selectedLube = lubes.find(l => l.id === Number(e.target.value));
                      setNewLubeSale({
                        ...newLubeSale, 
                        lube_id: e.target.value,
                        rate: selectedLube ? selectedLube.rate : ''
                      });
                    }}
                  >
                    <option value="">Select Lube/Oil...</option>
                    {lubes.map(l => (
                      <option key={l.id} value={l.id}>{l.name} ({l.pack_size}) - ₹{l.rate}</option>
                    ))}
                  </select>
                </div>
                <div className="w-full md:w-32">
                  <input 
                    required 
                    type="number" 
                    placeholder="Qty" 
                    className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none"
                    value={newLubeSale.quantity}
                    onChange={e => setNewLubeSale({...newLubeSale, quantity: e.target.value})}
                  />
                </div>
                <div className="w-full md:w-32">
                  <input 
                    type="number" 
                    step="0.01"
                    required
                    placeholder="Rate ₹" 
                    className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none"
                    value={newLubeSale.rate}
                    onChange={e => setNewLubeSale({...newLubeSale, rate: e.target.value})}
                  />
                </div>
                <button type="submit" className="bg-amber-500 hover:bg-amber-600 text-white font-bold px-6 py-3 rounded-xl transition-colors shrink-0">
                  Add Sale
                </button>
              </form>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50 text-slate-500 font-semibold border-b border-slate-100">
                    <tr>
                      <th className="p-3">Item</th>
                      <th className="p-3 text-center">Qty</th>
                      <th className="p-3 text-right">Rate</th>
                      <th className="p-3 text-right">Amount</th>
                      <th className="p-3 text-right"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {lubeSales.length === 0 ? (
                      <tr><td colSpan="5" className="p-4 text-center text-slate-500 italic">No lube sales recorded today</td></tr>
                    ) : (
                      lubeSales.map((sale) => (
                        <tr key={sale.id} className="hover:bg-slate-50">
                          <td className="p-3 font-semibold text-slate-700">{sale.name} ({sale.pack_size})</td>
                          <td className="p-3 font-mono text-center">{sale.quantity}</td>
                          <td className="p-3 font-mono text-right">
                            {Number(sale.rate || sale.selling_rate || sale.master_rate) !== Number(sale.master_rate) ? (
                              <div className="flex flex-col items-end">
                                <span className="font-bold text-red-500">₹{sale.rate || sale.selling_rate || sale.master_rate}</span>
                                <span className="text-[10px] bg-red-100 text-red-700 px-1.5 rounded font-sans tracking-wide border border-red-200 mt-0.5">
                                  ⚠️ OVERRIDE (Master: ₹{sale.master_rate})
                                </span>
                              </div>
                            ) : (
                              <span className="text-slate-600">₹{sale.master_rate}</span>
                            )}
                          </td>
                          <td className="p-3 font-mono font-bold text-slate-900 text-right">₹{sale.amount}</td>
                          <td className="p-3 text-right">
                            <button onClick={() => handleDelete('lubes', sale.id)} className="text-red-400 hover:text-red-600 p-1 rounded-md transition-colors hover:bg-red-50">
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
            <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4">Final Settlement Check</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center text-slate-600">
                <span>Accounted In Systems (Cards/UPI/Credit) + Bank Deposit</span>
                <span className="font-mono">₹{totalSettlementBox.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between items-center text-slate-600">
                <span>Expenses Paid from Cash</span>
                <span className="font-mono text-red-500">₹{totalExpenses.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between items-center text-slate-600">
                <span>Employee Shortages Accounted</span>
                <span className="font-mono text-amber-500">₹{totalEmployeeShortages.toLocaleString('en-IN')}</span>
              </div>
              <div className="h-px bg-slate-200 my-2"></div>
              <div className="flex justify-between items-center text-xl font-bold text-slate-800">
                <span>Total Accounted For</span>
                <span>₹{totalSettled.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between items-center text-2xl font-black mt-4 p-4 rounded-xl bg-indigo-50 text-indigo-700 border border-indigo-200">
                <span>Expected Closing Cash (In Drawer)</span>
                <span>₹{expectedClosingCash.toLocaleString('en-IN')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Expenses & Stock */}
        <div className="space-y-6">
          {/* Expenses */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="p-5 border-b border-slate-100 bg-slate-50">
              <h2 className="font-bold text-slate-800 flex items-center gap-2">
                <IndianRupee className="text-red-500" size={20} />
                Daily Expenses
              </h2>
            </div>
            <div className="p-5">
              <div className="space-y-3 mb-4 border-b border-slate-100 pb-4">
                <div className="flex items-center justify-between">
                  <label className="font-semibold text-slate-700 w-32 text-sm">DENSITY</label>
                  <input type="number" step="0.01" className="input-field flex-1 max-w-[150px]" value={regularExpenses.density} onChange={e => setRegularExpenses({...regularExpenses, density: e.target.value})} placeholder="0.00" />
                </div>
                <div className="flex items-center justify-between">
                  <label className="font-semibold text-slate-700 w-32 text-sm">FOOD AND TEA</label>
                  <input type="number" step="0.01" className="input-field flex-1 max-w-[150px]" value={regularExpenses.food} onChange={e => setRegularExpenses({...regularExpenses, food: e.target.value})} placeholder="0.00" />
                </div>
                <div className="flex items-center justify-between">
                  <label className="font-semibold text-slate-700 w-32 text-sm">TANKER EXPENSE</label>
                  <input type="number" step="0.01" className="input-field flex-1 max-w-[150px]" value={regularExpenses.tanker} onChange={e => setRegularExpenses({...regularExpenses, tanker: e.target.value})} placeholder="0.00" />
                </div>
              </div>

              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Other Expenses</h3>
              <form onSubmit={handleAddExpense} className="flex gap-2 mb-4">
                <input required placeholder="Description..." className="input-field flex-1 text-sm" value={newExpense.description} onChange={e => setNewExpense({...newExpense, description: e.target.value})} />
                <input required type="number" placeholder="₹" className="input-field w-24 text-sm" value={newExpense.amount} onChange={e => setNewExpense({...newExpense, amount: e.target.value})} />
                <button type="submit" className="btn-primary px-3"><Plus size={16}/></button>
              </form>
              <div className="space-y-2 mb-4">
                {expenses.map(e => (
                  <div key={e.id} className="flex justify-between items-center p-2 bg-slate-50 rounded-lg border border-slate-100 text-sm">
                    <span className="font-medium text-slate-700">{e.description}</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold text-slate-900">₹{e.amount}</span>
                      <button onClick={() => handleDelete('expenses', e.id)} className="text-slate-400 hover:text-red-500"><Trash2 size={14}/></button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="h-px bg-slate-200 my-2"></div>
              <div className="flex items-center justify-between font-bold text-lg text-slate-800">
                <span>TOTAL</span>
                <span>₹{totalExpenses.toLocaleString('en-IN')}</span>
              </div>
            </div>
          </div>

          {/* Daily Settlements / Deposits */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="p-5 border-b border-slate-100 bg-slate-50">
              <h2 className="font-bold text-slate-800 flex items-center gap-2">
                <Landmark className="text-indigo-500" size={20} />
                Day End Settlements
              </h2>
            </div>
            <div className="p-5">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="font-semibold text-slate-700 w-32">CREDIT SALE</label>
                  <input type="number" step="0.01" className="input-field flex-1 max-w-[200px]" value={settlements.credit} onChange={e => setSettlements({...settlements, credit: e.target.value})} placeholder="0.00" />
                </div>
                <div className="flex items-center justify-between">
                  <label className="font-semibold text-slate-700 w-32">PAYTM SALE</label>
                  <input type="number" step="0.01" className="input-field flex-1 max-w-[200px]" value={settlements.paytm} onChange={e => setSettlements({...settlements, paytm: e.target.value})} placeholder="0.00" />
                </div>
                <div className="flex items-center justify-between">
                  <label className="font-semibold text-slate-700 w-32">ICICI SALE</label>
                  <input type="number" step="0.01" className="input-field flex-1 max-w-[200px]" value={settlements.icici} onChange={e => setSettlements({...settlements, icici: e.target.value})} placeholder="0.00" />
                </div>
                <div className="flex items-center justify-between">
                  <label className="font-semibold text-slate-700 w-32">CCMS SALE</label>
                  <input type="number" step="0.01" className="input-field flex-1 max-w-[200px]" value={settlements.ccms} onChange={e => setSettlements({...settlements, ccms: e.target.value})} placeholder="0.00" />
                </div>
                <div className="flex items-center justify-between">
                  <label className="font-semibold text-slate-700 w-32">BANK DEPOSIT</label>
                  <input type="number" step="0.01" className="input-field flex-1 max-w-[200px]" value={settlements.bank} onChange={e => setSettlements({...settlements, bank: e.target.value})} placeholder="0.00" />
                </div>
                <div className="h-px bg-slate-200 my-3"></div>
                <div className="flex items-center justify-between font-bold text-lg text-slate-800">
                  <span className="w-32">TOTAL</span>
                  <span>₹{totalSettlementBox.toLocaleString('en-IN')}</span>
                </div>
              </div>
            </div>
          </div>
          {/* Employee Shortages */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="p-5 border-b border-slate-100 bg-red-50 flex justify-between items-center">
              <h2 className="font-bold text-red-700 flex items-center gap-2">
                <Wallet className="text-red-500" size={20} />
                Employee Shortages
              </h2>
            </div>
            <div className="p-5">
              <form onSubmit={handleAddCollection} className="flex gap-3 mb-6">
                <select required className="input-field flex-1" value={newCollection.employee_id} onChange={e => {
                  const emp = employees.find(x => x.id === Number(e.target.value));
                  if(emp) setNewCollection({...newCollection, employee_id: emp.id, employee_name: emp.name});
                }}>
                  <option value="">Select Employee...</option>
                  {employees.filter(emp => emp.status === 'Active').map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.name}</option>
                  ))}
                </select>
                <input type="number" step="0.01" required placeholder="Shortage ₹" className="input-field w-32" value={newCollection.shortage} onChange={e => setNewCollection({...newCollection, shortage: e.target.value})} />
                <button type="submit" className="bg-red-500 hover:bg-red-600 text-white font-bold px-4 py-3 rounded-xl transition-colors shrink-0 flex items-center justify-center"><Plus size={20}/></button>
              </form>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50 text-slate-500 font-semibold">
                    <tr>
                      <th className="p-3 rounded-tl-lg">Employee Name</th>
                      <th className="p-3">Shortage Amount</th>
                      <th className="p-3 rounded-tr-lg"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {collections.length === 0 ? (
                      <tr><td colSpan="3" className="p-4 text-center text-slate-500 italic">No shortages recorded</td></tr>
                    ) : (
                      collections.map(c => (
                        <tr key={c.id} className="hover:bg-slate-50 transition-colors">
                          <td className="p-3 font-semibold text-slate-800">{c.employee_name}</td>
                          <td className="p-3 font-mono font-bold text-red-600">₹{c.shortage}</td>
                          <td className="p-3 text-right">
                            <button onClick={() => handleDelete('collections', c.id)} className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg"><Trash2 size={16}/></button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <div className="h-px bg-slate-200 my-3"></div>
              <div className="flex items-center justify-between font-bold text-lg text-slate-800">
                <span>TOTAL</span>
                <span className="text-red-600">₹{totalEmployeeShortages.toLocaleString('en-IN')}</span>
              </div>
            </div>
          </div>

        </div>

      </div>
      </div>
    </div>
  );
};

export default DailySheet;
